import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { FunctionComponent } from 'react';

import { BannerAttention, BannerSize, BannerStyle } from '../../../../../typings/enums';
import useCompleteTotpSetup from '../../../../hooks/api/auth/mfa/useCompleteTotpSetup';
import useInitTotpSetup from '../../../../hooks/api/auth/mfa/useInitTotpSetup';
import Banner from '../../../generic/Banner';
import Button from '../../../generic/Button/Button';
import { BUTTON_TYPE } from '../../../generic/Button/button-constants';
import CardBanner, { CardBannerVariant } from '../../../generic/CardBanner/CardBanner';
import CopyToClipboard from '../../../generic/CopyToClipboard';
import Flex from '../../../generic/Flex';
import VerificationCodeInput from '../../../generic/Forms/VerificationCodeInput';
import Loader from '../../../generic/Loader';

import BackupCodes from './BackupCodes';

import styles from './TotpSetupModal.module.less';

type Props = {
  show: boolean;
  onHide: () => void;
  resetBackupCodes: () => void;
};

type SetupStep = 'scan' | 'verify' | 'backup-codes';

/**
 * Modal for setting up TOTP-based MFA.
 * Guides user through scanning QR code, verifying setup, and saving backup codes.
 */
const TotpSetupModal: FunctionComponent<Props> = ({ show, onHide, resetBackupCodes }) => {
  const [step, setStep] = useState<SetupStep>('scan');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [setupData, setSetupData] = useState<{
    secret: string;
    qrCode: string;
  } | null>(null);

  const initMutation = useInitTotpSetup();
  const completeMutation = useCompleteTotpSetup();

  const handleModalShow = async () => {
    resetBackupCodes();
    if (!setupData) {
      try {
        const { data } = await initMutation.mutateAsync();
        if (data) {
          setSetupData({
            secret: data.secret,
            qrCode: data.qrCode,
          });
        }
        setStep('scan');
      } catch (error) {
        // Error toast handled by useToastedMutation
        onHide();
      }
    }
  };

  const handleComplete = async (codeValue?: string) => {
    if (!setupData) {
      return;
    }

    // Use provided code value or fall back to state (for button clicks)
    const code = codeValue || verificationCode;

    // Validate code length before sending
    if (code.length !== 6) {
      return;
    }

    try {
      const { data } = await completeMutation.mutateAsync({
        secret: setupData.secret,
        code,
      });
      // Success toast handled by useToastedMutation
      // Store backup codes and show them to user
      setBackupCodes(data?.method.backupCodes || []);
      setStep('backup-codes');
    } catch (error) {
      // Error toast handled by useToastedMutation
      // Clear the input on failure so user can try again
      setVerificationCode('');
    }
  };

  const handleClose = () => {
    setStep('scan');
    setVerificationCode('');
    setBackupCodes([]);
    setSetupData(null);
    onHide();
  };

  const isLoading = initMutation.isPending || completeMutation.isPending;

  return (
    <Modal show={show} onHide={handleClose} onEnter={handleModalShow}>
      <Modal.Header closeButton>
        <Modal.Title>Set Up Authenticator App</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && <Loader />}

        {!isLoading && step === 'scan' && setupData && (
          <div>
            <div>
              Scan this QR code with your authenticator app (such as Google Authenticator, Authy, or 1Password).
            </div>
            <div className="text-center margin-bottom-lg">
              <img src={setupData.qrCode} alt="QR Code" className={styles.qrCode} />
            </div>
            <CardBanner
              title="Can&apos;t scan the QR code?"
              variant={CardBannerVariant.DANGER}
            >
              <div className="margin-top-sm">
                <Flex direction="column" align="flex-start" justify="flex-start">
                  <div>
                    Enter this secret key manually:
                  </div>
                  <CopyToClipboard text={setupData.secret}>
                    <code>{setupData.secret}</code>
                  </CopyToClipboard>
                </Flex>
              </div>
            </CardBanner>
          </div>
        )}

        {!isLoading && step === 'verify' && (
          <div>
            <Banner
              body=""
              bannerStyle={BannerStyle.Info}
              bannerSize={BannerSize.Small}
              bannerAttention={BannerAttention.Low}
              title="Enter the 6-digit code from your authenticator app to verify setup."
            />
            <div className="margin-top-lg margin-bottom-lg">
              <VerificationCodeInput
                length={6}
                value={verificationCode}
                onChange={setVerificationCode}
                onComplete={handleComplete}
                disabled={isLoading}
              />
            </div>
          </div>
        )}

        {!isLoading && step === 'backup-codes' && backupCodes.length > 0 && (
          <div>
            <Banner
              body=""
              bannerStyle={BannerStyle.Success}
              bannerSize={BannerSize.Small}
              bannerAttention={BannerAttention.Low}
              title="MFA setup complete! Your account is now more secure."
            />
            <BackupCodes codes={backupCodes} />
          </div>
        )}

      </Modal.Body>
      <Modal.Footer>
        <Flex gap="4px" justify="flex-end">
          {step !== 'backup-codes' && (
            <Button type={BUTTON_TYPE.OUTLINE} onClick={handleClose} isDisabled={isLoading}>
              Cancel
            </Button>
          )}
          {step === 'scan' && (
            <Button type={BUTTON_TYPE.PRIMARY} onClick={() => setStep('verify')} isDisabled={isLoading}>
              Next: Verify Setup
            </Button>
          )}
          {step === 'verify' && (
            <Button
              type={BUTTON_TYPE.PRIMARY}
              onClick={() => handleComplete()}
              isDisabled={isLoading || verificationCode.length !== 6}
            >
              Verify and Complete Setup
            </Button>
          )}
          {step === 'backup-codes' && (
            <Button type={BUTTON_TYPE.PRIMARY} onClick={handleClose} isDisabled={isLoading}>
              Done
            </Button>
          )}
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};

export default TotpSetupModal;
